(() => {
  const formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    const withDelimiters = (decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(2, "'", '.');
        default: return withDelimiters(2, ',', '.');
      }
    });
  };

  // Gallery ------------------------------------------------------------------
  const initGallery = (root) => {
    const gallery = root.querySelector('[data-gallery]');
    if (!gallery) return { show: () => {} };
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = Array.from(track.children);
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    const counter = gallery.querySelector('[data-gallery-current]');
    let current = 0;

    const mark = (index) => {
      current = index;
      thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
      if (counter) counter.textContent = String(index + 1);
      const active = thumbs[index];
      if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    };

    const show = (index, smooth = true) => {
      const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!target) return;
      track.scrollTo({ left: target.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      mark(slides.indexOf(target));
    };

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        if (index !== current) mark(index);
      });
    }, { passive: true });

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => show(Number(thumb.dataset.galleryThumb))));
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', () => show(current === 0 ? slides.length - 1 : current - 1));
    if (next) next.addEventListener('click', () => show(current === slides.length - 1 ? 0 : current + 1));
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') show(current - 1);
      if (event.key === 'ArrowRight') show(current + 1);
    });

    const showMedia = (mediaId, smooth) => {
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) show(index, smooth);
    };
    return { show, showMedia };
  };

  // Product form ----------------------------------------------------------------
  const initProduct = (root) => {
    const dataEl = root.querySelector('[data-product-json]');
    if (!dataEl) return;
    const data = JSON.parse(dataEl.textContent);
    const gallery = initGallery(root);

    const idInput = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-add-button]');
    const priceWrap = root.querySelector('[data-price]');
    const priceCurrent = root.querySelector('[data-price-current]');
    const priceCompare = root.querySelector('[data-price-compare]');
    const priceSave = root.querySelector('[data-price-save]');
    const saveLabelEl = root.querySelector('[data-save-label]');
    const saveLabel = saveLabelEl ? saveLabelEl.innerHTML.trim() : 'Save';
    const sku = root.querySelector('[data-sku]');
    const skuWrap = root.querySelector('[data-sku-wrapper]');
    const inventory = root.querySelector('[data-inventory]');
    const inventoryText = root.querySelector('[data-inventory-text]');
    const labelsEl = root.querySelector('[data-inventory-labels]');
    const labels = labelsEl ? JSON.parse(labelsEl.textContent) : null;
    const addLabel = addButton ? addButton.textContent.trim() : '';
    const soldOutLabel = labels && labels.sold_out ? labels.sold_out : 'Sold out';

    const currentId = idInput ? Number(idInput.value) : null;
    let variant = data.variants.find((v) => v.id === currentId) || data.variants[0];

    const renderInventory = (v) => {
      if (!inventory || !labels) return;
      let state = 'in_stock';
      let text = labels.in_stock;
      if (!v.available) {
        state = 'sold_out'; text = labels.sold_out;
      } else if (v.inventory_management === 'shopify' && v.inventory_quantity <= 0 && v.inventory_policy === 'continue') {
        state = 'backorder'; text = labels.backorder;
      } else if (v.inventory_management === 'shopify' && v.inventory_quantity > 0 && v.inventory_quantity <= labels.threshold && labels.low_stock) {
        state = 'low_stock'; text = labels.low_stock.replace('[count]', v.inventory_quantity);
      }
      inventory.dataset.state = state;
      inventoryText.textContent = text;
      inventory.hidden = !text;
    };

    const render = (v, updateMedia) => {
      if (idInput) idInput.value = v.id;
      if (priceCurrent) priceCurrent.textContent = formatMoney(v.price, data.money_format);
      const onSale = v.compare_at_price > v.price;
      if (priceWrap) priceWrap.classList.toggle('pdp-price--sale', onSale);
      if (priceCompare) priceCompare.textContent = onSale ? formatMoney(v.compare_at_price, data.money_format) : '';
      if (priceSave) {
        const saved = formatMoney(v.compare_at_price - v.price, data.money_format).replace(/[.,]00(?=\D*$)/, '');
        priceSave.textContent = onSale ? `${saveLabel} ${saved}` : '';
      }
      if (sku) sku.textContent = v.sku || '';
      if (skuWrap) skuWrap.classList.toggle('is-hidden', !v.sku);
      if (addButton) {
        addButton.disabled = !v.available;
        addButton.textContent = v.available ? addLabel : soldOutLabel;
      }
      renderInventory(v);
      if (updateMedia && v.media_id) gallery.showMedia(v.media_id, true);
    };

    // Variant pills: pick the variant matching every selected option.
    const picker = root.querySelector('[data-variant-picker]');
    if (picker) {
      const optionInputs = Array.from(picker.querySelectorAll('input[type="radio"]'));
      const optionCount = data.variants[0] ? data.variants[0].options.length : 0;

      const syncAvailability = () => {
        // Grey out values that have no available variant combined with the other current choices.
        optionInputs.forEach((input) => {
          const index = Number(input.dataset.optionIndex);
          const match = data.variants.some((v) => v.available && v.options.every((value, i) => (i === index ? value === input.value : value === variant.options[i])));
          input.closest('.pdp-pill').classList.toggle('is-unavailable', !match);
        });
      };

      picker.addEventListener('change', () => {
        const selected = [];
        for (let i = 0; i < optionCount; i += 1) {
          const checked = picker.querySelector(`input[data-option-index="${i}"]:checked`);
          selected.push(checked ? checked.value : null);
        }
        const found = data.variants.find((v) => v.options.every((value, i) => value === selected[i]));
        selected.forEach((value, i) => {
          const label = picker.querySelector(`[data-option-value="${i}"]`);
          if (label) label.textContent = value;
        });
        if (!found) {
          if (addButton) { addButton.disabled = true; addButton.textContent = soldOutLabel; }
          return;
        }
        variant = found;
        render(variant, true);
        syncAvailability();
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      });
      syncAvailability();
    }

    render(variant, false);
    if (variant.media_id) gallery.showMedia(variant.media_id, false);

    // Quantity stepper
    const qty = root.querySelector('.pdp-qty__input');
    root.querySelectorAll('[data-qty-minus], [data-qty-plus]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.hasAttribute('data-qty-plus') ? 1 : -1;
        qty.value = Math.max(1, (Number(qty.value) || 1) + step);
      });
    });

    // Add to cart without leaving the page; the form still posts normally if JS fails.
    const form = root.querySelector('[data-product-form]');
    const errorEl = root.querySelector('[data-form-error]');
    const added = root.querySelector('[data-added-message]');
    if (form && window.fetch) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!addButton || addButton.disabled) return;
        addButton.classList.add('is-loading');
        addButton.disabled = true;
        if (errorEl) errorEl.hidden = true;
        try {
          const response = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || 'Could not add to cart.');
          const cart = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart.js`).then((r) => r.json());
          document.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
            el.dataset.count = cart.item_count;
          });
          if (added) {
            added.hidden = false;
            clearTimeout(added._timer);
            added._timer = setTimeout(() => { added.hidden = true; }, 6000);
          }
        } catch (error) {
          if (errorEl) { errorEl.textContent = error.message; errorEl.hidden = false; }
        } finally {
          addButton.classList.remove('is-loading');
          addButton.disabled = !variant.available;
        }
      });
    }

    // Remember this product for the "Recently viewed" rail.
    try {
      const key = 'elva:recently-viewed';
      const handle = root.dataset.productHandle;
      const list = JSON.parse(localStorage.getItem(key) || '[]').filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
    } catch (e) { /* storage unavailable */ }
  };

  // Recommendations ----------------------------------------------------------
  const initRecommendations = (el) => {
    if (!el.dataset.url || el.children.length) return;
    fetch(el.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) { el.innerHTML = fresh.innerHTML; el.hidden = false; }
      })
      .catch(() => { el.hidden = true; });
  };

  // Recently viewed ------------------------------------------------------------
  const initRecentlyViewed = async (el) => {
    let handles = [];
    try { handles = JSON.parse(localStorage.getItem('elva:recently-viewed') || '[]'); } catch (e) { return; }
    handles = handles.filter((h) => h !== el.dataset.current).slice(0, Number(el.dataset.limit) || 4);
    if (!handles.length) return;
    const grid = el.querySelector('[data-recently-viewed-grid]');
    const root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
    const cards = await Promise.all(handles.map((h) =>
      fetch(`${root}products/${encodeURIComponent(h)}?view=card`).then((r) => (r.ok ? r.text() : '')).catch(() => '')
    ));
    const html = cards.filter((c) => c.includes('product-card')).join('');
    if (!html) return;
    grid.innerHTML = html;
    el.hidden = false;
  };

  const init = (scope) => {
    scope.querySelectorAll('[data-product]').forEach(initProduct);
    scope.querySelectorAll('[data-recommendations]').forEach(initRecommendations);
    scope.querySelectorAll('[data-recently-viewed]').forEach(initRecentlyViewed);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
